import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiCheckbox, MiModalConfirm } from "@miplatform/components";
import { BUTTON_COLORS } from "@miplatform/constants";
import { bugsnag } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Typography } from "@mui/material";
import {
  INVOICE_ITEM_TYPE,
  RECONCILIATION_STEPS,
} from "../../accountingConstants";
import { useReconcileContext } from "../ReconcileContext";
import { AccountingInvoiceItem } from "./AccountingInvoiceItem";

export const AccountingVariance = ({ variance }) => {
  const {
    invoicesModule,
    activeStep,
    fetchAndEnsureDraftInvoice,
    getPurchaseOrders,
    isInvoiceMode,
    verificationRef,
    verificationState,
    updateInvoiceFormState,
  } = useReconcileContext();
  const { classes, theme } = useStyles({ activeStep });

  const [varianceItems, setVarianceItems] = useState([]);

  const [confirmModalConfig, setConfirmModalConfig] = useState({
    open: false,
    title: "",
    description: "",
    textPositive: "Confirm",
    textNegative: "Cancel",
    handlePositive: () => {},
    handleNegative: () =>
      setConfirmModalConfig((prev) => ({ ...prev, open: false })),
  });

  const {
    originatingInvoice: { invoiceName },
  } = variance || {};

  useEffect(() => {
    if (!verificationRef.current) {
      verificationRef.current = new Map();
    }

    const updatedItems = (variance?.varianceItems || []).map((item) => {
      const isVerified = verificationRef.current.get(item.id);
      if (isVerified === undefined) {
        verificationRef.current.set(item.id, false);
      }
      return isVerified !== undefined ? { ...item, isVerified } : item;
    });

    setVarianceItems(updatedItems);
  }, [variance?.varianceItems, verificationRef]);

  const handleDeleteInvoiceItem = async (invoiceItem) => {
    const executeDelete = async () => {
      setVarianceItems((prev) =>
        prev.map((item) =>
          item.id === invoiceItem.id ? { ...item, isSubmitting: true } : item
        )
      );
      const currentInvoiceId = invoiceItem.invoiceId;
      const payingInvoiceItem = invoiceItem.payingInvoiceItem.find(
        (item) => item.invoiceId === currentInvoiceId
      );

      const response = await invoicesModule.deleteItem({
        id: payingInvoiceItem.id,
        modifiedOn: payingInvoiceItem.modifiedOn,
      });
      if (response) {
        updateInvoiceFormState(invoiceItem.id, null);
        fetchAndEnsureDraftInvoice();
        invoicesModule.getOrderVariances();
        verificationRef.current.delete(invoiceItem.id);

        getPurchaseOrders();
      } else {
        setVarianceItems((prev) =>
          prev.map((item) =>
            item.id === invoiceItem.id ? { ...item, isSubmitting: false } : item
          )
        );
      }
    };
    showConfirmModal({
      title: "Delete Variance Item?",
      description:
        "Are you sure you want to delete this variance item? This action cannot be undone.",
      confirmButtonText: "Delete",
      titleIcon: {
        path: mdi.mdiTrashCanOutline,
        color: theme.palette.error.main,
      },
      onConfirm: executeDelete,
      buttonPositiveColor: BUTTON_COLORS.ERROR,
    });
  };

  const handleUpdateInvoiceItem = async (updatedItem) => {
    try {
      const isItemVerified = updatedItem.isVerified;

      const currentInvoiceId = updatedItem?.invoiceId;

      if (!updatedItem?.payingInvoiceItem.length > 0) {
        setVarianceItems((prev) =>
          prev.map((item) =>
            item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
          )
        );

        const response = await invoicesModule.createItem({
          data: {
            name: `${updatedItem.name} Invoice Variance`,

            paidOffVarianceId: updatedItem?.id,
            unitPrice: updatedItem.unitPrice, // thats the amount paid toward variance that user entered
            invoiceId: currentInvoiceId, // the one that selected in the dropdown
            section: "Accounting Variance",
            type: INVOICE_ITEM_TYPE.OTHER,
          },
        });
        if (!response || response.error) {
          setVarianceItems((prev) =>
            prev.map((item) =>
              item.id === updatedItem.id
                ? { ...item, isSubmitting: false }
                : item
            )
          );
          throw new Error(
            response?.error?.message || "Failed to create variance item"
          );
        }
        const createdItem = response.data;
        if (createdItem && createdItem.id) {
          if (isItemVerified) {
            verificationRef.current.set(createdItem.id, isItemVerified);
          }
          setVarianceItems((prev) => [
            ...prev,
            { ...createdItem, isVerified: isItemVerified },
          ]);
        }

        if (isItemVerified !== undefined) {
          verificationRef.current.set(updatedItem.id, isItemVerified);
        }
      } else {
        setVarianceItems((prev) =>
          prev.map((item) =>
            item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
          )
        );

        const payingInvoiceItem = updatedItem.payingInvoiceItem.find(
          (item) => item.invoiceId === currentInvoiceId
        );
        const updateResponse = await invoicesModule.updateItem({
          id: payingInvoiceItem.id,
          data: {
            unitPrice: updatedItem.unitPrice,
            invoiceId: updatedItem.invoiceId,
            modifiedOn: payingInvoiceItem.modifiedOn,
          },
        });

        if (!updateResponse || updateResponse.error) {
          setVarianceItems((prev) =>
            prev.map((item) =>
              item.id === updatedItem.id
                ? { ...item, isSubmitting: false }
                : item
            )
          );
          throw new Error(
            updateResponse?.error?.message || "Failed to update variance item"
          );
        }

        setVarianceItems((prev) =>
          prev.map((item) =>
            item.id === updatedItem.id
              ? { ...updatedItem, isSubmitting: false }
              : item
          )
        );
      }

      return true;
    } catch (error) {
      bugsnag.notify("Error updating variance item:", error);
      throw error;
    } finally {
      await Promise.all([
        fetchAndEnsureDraftInvoice(),
        getPurchaseOrders(),
        invoicesModule.getOrderVariances(),
      ]);
    }
  };

  const showConfirmModal = ({
    title,
    description,
    confirmButtonText = "Confirm",
    cancelButtonText = "",
    onConfirm,
    ...rest
  }) => {
    setConfirmModalConfig({
      open: true,
      title,
      description,
      textPositive: confirmButtonText,
      textNegative: cancelButtonText,
      handlePositive: () => {
        onConfirm();
        closeConfirmModal();
      },
      handleNegative: () => closeConfirmModal(),
      ...rest,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModalConfig((prev) => ({ ...prev, open: false }));
  };

  const countOfVerifiedItems = useMemo(() => {
    if (!varianceItems?.length) return 0;

    return varianceItems.filter((item) => verificationState[item.id] === true)
      .length;
  }, [varianceItems, verificationState]);

  const isInvoiceVerified = useMemo(() => {
    return (
      countOfVerifiedItems === varianceItems.length && varianceItems.length > 0
    );
  }, [countOfVerifiedItems, varianceItems]);

  return (
    <Box mx={4} mb={2}>
      <Box className={classes.cardContainer}>
        <Box className={classes.cardHeader}>
          <Typography
            variant="body1"
            fontWeight="bold"
            ml={2}
            color="text.primary"
          >
            {invoiceName ? `${invoiceName} Variance` : ""}
          </Typography>

          <Box ml="auto" mr={2} display="flex" alignItems="center" gap={4}>
            {isInvoiceMode && (
              <Box
                border="2px solid"
                borderColor={isInvoiceVerified ? "success.main" : "transparent"}
                backgroundColor={
                  isInvoiceVerified ? "green.light" : "grey.mediumLight"
                }
                borderRadius={1}
                px={2}
              >
                <MiCheckbox
                  value
                  color="success"
                  style={{ cursor: "default", padding: theme.spacing(1, 0) }}
                  disableRipple
                  readOnly
                  labelStyle={{
                    "& .MuiFormControlLabel-label": {
                      color: isInvoiceVerified ? "success.main" : "inherit",
                      fontWeight: "400",
                      paddingLeft: 1,
                    },
                  }}
                  label={`${countOfVerifiedItems}/${varianceItems.length} items verified`}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Box>

        {varianceItems.length > 0 ? (
          <Box className={classes.itemRow}>
            {varianceItems.map((item) => (
              <AccountingInvoiceItem
                key={item.id || item.tempId}
                item={item}
                isVarianceItem
                itemType="variance"
                onDelete={() => handleDeleteInvoiceItem(item)}
                onUpdate={handleUpdateInvoiceItem}
              />
            ))}
          </Box>
        ) : (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No variance items added yet.
            </Typography>
          </Box>
        )}
      </Box>
      <MiModalConfirm
        open={confirmModalConfig.open}
        title={confirmModalConfig.title}
        description={confirmModalConfig.description}
        textPositive={confirmModalConfig.textPositive}
        textNegative={confirmModalConfig.textNegative}
        handlePositive={confirmModalConfig.handlePositive}
        handleNegative={confirmModalConfig.handleNegative}
        buttonPositiveColor={confirmModalConfig.buttonPositiveColor}
        titleIcon={confirmModalConfig.titleIcon}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme, { activeStep }) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardContainer: {
    backgroundColor: theme.palette.grey.light,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey.accent}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(1, 0),
    padding: theme.spacing(3),
  },
  cardHeader: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(1),
  },
  gridHeader: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: "center",
  },
  gridHeaderCell: {
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRight: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderRight:
        activeStep === RECONCILIATION_STEPS.REVIEW_SUBMIT
          ? "none"
          : `1px solid ${theme.palette.divider}`,
    },
  },
  itemRow: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 4,
  },
}));
